import MarkerClusterGroup from '@changey/react-leaflet-markercluster';
import { useMessaging } from 'features/Responder/features/Messenger';
import { extractPersonnelIsSupervisor } from 'features/Responder/features/Personnel/features/PersonnelList/utils/personnelListUtils';
import { buildPersonnelMarkerIcon } from 'features/Responder/features/ResponderMap/utils/pinIcon';
import { useSmoothMarkerPosition } from 'features/Responder/features/Tours/features/TourMap/hooks/useSmoothMarkerPosition';
import { focusMapOnGeometry } from 'features/Responder/utils/focusMapOnGeometry';
import {
  getPersonnelLocationTimestamp,
  pickLatestPersonnelLocation,
} from 'features/Responder/utils/personnelLocation';
import L from 'leaflet';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Marker, Popup, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import PersonnelMarkerPanel from './PersonnelMarkerPanel';

// keep the map clean at wider zoom levels by clustering first.
const PERSONNEL_LABEL_MIN_ZOOM = 15;
// Approximate label footprint (in screen pixels) for overlap checks.
const LABEL_RECT_WIDTH = 128;
const LABEL_RECT_HEIGHT = 20;
const LABEL_TOP_OFFSET = 56;
const LABEL_COLLISION_GAP = 8;

const resolvePersonnelDisplayName = (person) => {
  const userId = person?.userId ?? person?.id;
  const firstName = person?.firstName ?? person?.first_name ?? '';
  const lastName = person?.lastName ?? person?.last_name ?? '';
  const combinedName = `${firstName} ${lastName}`.trim();

  return (
    person?.name ||
    person?.fullName ||
    combinedName ||
    person?.username ||
    (userId != null ? `Personnel #${userId}` : 'Personnel')
  );
};

const resolveParticipantUserId = (participant) =>
  participant?.user_id ?? participant?.userId ?? participant?.id ?? null;

const findDirectConversationForUser = ({ conversations, targetUserId, currentUserId }) => {
  const targetKey = `${targetUserId}`;
  const currentKey = currentUserId != null ? `${currentUserId}` : null;
  if (!Array.isArray(conversations)) return null;

  return (
    conversations.find((conversation) => {
      if (`${conversation?.type}` !== 'direct') return false;
      const participants = Array.isArray(conversation?.participants)
        ? conversation.participants
        : [];
      const hasTarget = participants.some(
        (participant) =>
          `${resolveParticipantUserId(participant)}` === targetKey && participant?.left_at == null,
      );
      if (!hasTarget) return false;
      if (currentKey == null) return true;
      return participants.some(
        (participant) =>
          `${resolveParticipantUserId(participant)}` === currentKey && participant?.left_at == null,
      );
    }) || null
  );
};

// rect = rectangle bounds of a label on the screen.
// We use this to avoid names stacking on top of each other.
const doLabelRectsOverlap = (a, b, gap = 0) =>
  !(
    a.right + gap < b.left ||
    a.left - gap > b.right ||
    a.bottom + gap < b.top ||
    a.top - gap > b.bottom
  );

const toPriorityScore = (entry, selectedPersonnelId) => {
  const selectedBoost = `${entry.id}` === `${selectedPersonnelId ?? ''}` ? 1_000_000 : 0;
  const onlineBoost = entry.clockedIn ? 100_000 : 0;
  const recencyBoost = Number.isFinite(entry.timestampValue) ? entry.timestampValue : 0;
  return selectedBoost + onlineBoost + recencyBoost;
};

const computeVisibleLabelIds = ({ entries, map, zoom, selectedPersonnelId }) => {
  // When zoomed out, labels create noise fast. Cluster view handles that case.
  if (!map || !Array.isArray(entries) || zoom < PERSONNEL_LABEL_MIN_ZOOM) return new Set();

  const container = map.getContainer?.();
  const viewportWidth = Number(container?.clientWidth) || 0;
  const viewportHeight = Number(container?.clientHeight) || 0;
  if (!viewportWidth || !viewportHeight) return new Set();

  const placedRects = [];
  const visibleIds = new Set();

  // Highest priority labels get first chance to render: selected, online, newest.
  [...entries]
    .sort(
      (a, b) => toPriorityScore(b, selectedPersonnelId) - toPriorityScore(a, selectedPersonnelId),
    )
    .forEach((entry) => {
      if (!Array.isArray(entry.latLng)) return;
      const point = map.latLngToContainerPoint(entry.latLng);
      if (!point) return;

      const rect = {
        left: point.x - LABEL_RECT_WIDTH / 2,
        right: point.x + LABEL_RECT_WIDTH / 2,
        top: point.y - LABEL_TOP_OFFSET,
        bottom: point.y - LABEL_TOP_OFFSET + LABEL_RECT_HEIGHT,
      };

      const isOffscreen =
        rect.right < 0 || rect.left > viewportWidth || rect.bottom < 0 || rect.top > viewportHeight;
      if (isOffscreen) return;

      const overlaps = placedRects.some((placedRect) =>
        doLabelRectsOverlap(rect, placedRect, LABEL_COLLISION_GAP),
      );
      // If this one would stack on another label, skip it to keep things readable.
      if (overlaps) return;

      placedRects.push(rect);
      visibleIds.add(`${entry.id}`);
    });

  return visibleIds;
};

const PersonnelMarker = ({
  person,
  personId,
  displayName,
  latLng,
  timestampLabel,
  showLabel,
  showHoverTooltip,
  onSelect,
  isPanelOpen,
  onPanelOpen,
  onPanelClose,
  panelContent,
}) => {
  const map = useMap();
  const smoothPosition = useSmoothMarkerPosition(latLng);
  const markerRef = useRef(null);
  const isSupervisor = extractPersonnelIsSupervisor(person);

  const markerIcon = useMemo(
    () =>
      buildPersonnelMarkerIcon({
        person,
        label: displayName,
        showLabel,
      }),
    [displayName, person, showLabel],
  );

  useEffect(() => {
    const marker = markerRef.current;
    if (!marker || !panelContent) return;
    if (isPanelOpen) {
      marker.openPopup();
      return;
    }
    marker.closePopup();
  }, [isPanelOpen, panelContent]);

  if (!smoothPosition) return null;

  return (
    <Marker
      ref={markerRef}
      position={smoothPosition}
      clockedIn={Boolean(person?.clockedIn)}
      icon={markerIcon}
      eventHandlers={{
        click: () => {
          if (!map || !latLng) return;
          onSelect?.(personId);
          onPanelOpen?.(personId);
          focusMapOnGeometry(map, [latLng, latLng], {
            padding: [80, 80],
            maxZoom: 16,
            animate: true,
          });
        },
        popupclose: () => {
          onPanelClose?.();
        },
      }}
    >
      {showHoverTooltip && !isPanelOpen && (
        <Tooltip className="responder-tooltip" direction="top" offset={[0, -10]} opacity={0.8}>
          <div>
            <div>{displayName}</div>
            {isSupervisor && <div>Supervisor</div>}
            <div>{person.clockedIn ? 'Clocked In' : 'Offline'}</div>
            <div>Last Update: {timestampLabel}</div>
          </div>
        </Tooltip>
      )}
      {panelContent && (
        <Popup className="responder-personnel-panel-popup" closeButton autoPan>
          {panelContent}
        </Popup>
      )}
    </Marker>
  );
};

/**
 * Render personnel markers with status tooltips.
 */
const PersonellLayer = ({
  personnel,
  panelComponent: PanelComponent = PersonnelMarkerPanel,
  panelPropsByPerson = null,
  showHoverTooltip = true,
}) => {
  const map = useMap();
  const {
    conversations,
    createConversation,
    selectConversation,
    currentUserId,
    isMessengerEnabled,
  } = useMessaging();
  const [selectedPersonnelId, setSelectedPersonnelId] = useState(null);
  const [activePanelPersonnelId, setActivePanelPersonnelId] = useState(null);
  const [pendingMessageUserId, setPendingMessageUserId] = useState(null);
  const [mapZoom, setMapZoom] = useState(() => Number(map?.getZoom?.()) || 0);
  // Multiple people can be moving at once; re-check label placement whenever viewport changes.
  const [viewportChangeTick, setViewportChangeTick] = useState(0);

  useMapEvents({
    zoomend: () => {
      const nextZoom = Number(map?.getZoom?.()) || 0;
      setMapZoom(nextZoom);
      setViewportChangeTick((prev) => prev + 1);
    },
    moveend: () => {
      setViewportChangeTick((prev) => prev + 1);
    },
    resize: () => {
      setViewportChangeTick((prev) => prev + 1);
    },
    click: () => {
      setActivePanelPersonnelId(null);
    },
  });

  const entries = useMemo(() => {
    if (!personnel) return [];
    return Object.values(personnel)
      .map((person, index) => {
        const { latLng, loc } = pickLatestPersonnelLocation(person);
        if (!latLng) return null;
        const id = person?.userId ?? person?.id ?? `index-${index}`;
        const timestampValue = getPersonnelLocationTimestamp(loc);
        return {
          id,
          person,
          latLng,
          displayName: resolvePersonnelDisplayName(person),
          clockedIn: Boolean(person?.clockedIn),
          timestampValue,
          timestampLabel:
            timestampValue != null ? new Date(timestampValue).toLocaleString() : 'No timestamp',
        };
      })
      .filter(Boolean);
  }, [personnel]);

  // Hybrid behavior:
  // zoomed out: clusters for clarity
  // zoomed in: individual markers with non-overlapping names
  const showClusters = mapZoom < PERSONNEL_LABEL_MIN_ZOOM;

  const visibleLabelIds = useMemo(
    () =>
      computeVisibleLabelIds({
        entries,
        map,
        zoom: mapZoom,
        selectedPersonnelId,
      }),
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [entries, map, mapZoom, selectedPersonnelId, viewportChangeTick],
  );

  const clusterIconCreateFunction = useMemo(
    () => (cluster) => {
      const markers = cluster.getAllChildMarkers();
      const onlineCount = markers.reduce(
        (count, marker) => count + (marker?.options?.clockedIn ? 1 : 0),
        0,
      );
      const totalCount = cluster.getChildCount();
      return L.divIcon({
        className: 'responder-personnel-cluster-wrapper',
        html: `
          <div class="responder-personnel-cluster">
            <div class="responder-personnel-cluster-count">${totalCount}</div>
            <div class="responder-personnel-cluster-meta">${onlineCount} online</div>
          </div>
        `,
        iconSize: [50, 50],
        iconAnchor: [25, 25],
      });
    },
    [],
  );

  const openMessengerShell = useCallback(() => {
    if (!isMessengerEnabled) return;
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('responder-messenger-open'));
  }, [isMessengerEnabled]);

  const handleMessagePerson = useCallback(
    async (personId) => {
      if (!isMessengerEnabled) return;
      const normalizedPersonId = Number(personId);
      if (!Number.isFinite(normalizedPersonId)) return;
      if (`${normalizedPersonId}` === `${currentUserId ?? ''}`) return;

      setPendingMessageUserId(normalizedPersonId);
      try {
        let conversationId = null;
        const existing = findDirectConversationForUser({
          conversations,
          targetUserId: normalizedPersonId,
          currentUserId,
        });

        if (existing?.id != null) {
          conversationId = existing.id;
        } else {
          const created = await createConversation({
            type: 'direct',
            participants: [normalizedPersonId],
            participant_ids: [normalizedPersonId],
            participantIds: [normalizedPersonId],
          });
          conversationId = created?.id ?? null;
        }

        if (conversationId != null) {
          selectConversation(conversationId);
        }
        openMessengerShell();
      } catch (error) {
        console.error('[PersonellLayer] Failed to open direct conversation:', error);
      } finally {
        setPendingMessageUserId(null);
      }
    },
    [
      conversations,
      createConversation,
      currentUserId,
      isMessengerEnabled,
      openMessengerShell,
      selectConversation,
    ],
  );

  const markers = entries.map((entry) => {
    const isPanelOpen = `${activePanelPersonnelId ?? ''}` === `${entry.id}`;
    const panelProps =
      typeof panelPropsByPerson === 'function'
        ? panelPropsByPerson(entry.person, entry)
        : panelPropsByPerson;

    const panelContent = PanelComponent ? (
      <PanelComponent
        person={entry.person}
        personId={entry.id}
        displayName={entry.displayName}
        timestampLabel={entry.timestampLabel}
        isClockedIn={entry.clockedIn}
        isCurrentUser={`${entry.id}` === `${currentUserId ?? ''}`}
        messagePending={`${pendingMessageUserId ?? ''}` === `${entry.id}`}
        showMessageAction={isMessengerEnabled}
        onMessage={() => handleMessagePerson(entry.id)}
        closePanel={() => setActivePanelPersonnelId(null)}
        {...(panelProps || {})}
      />
    ) : null;

    return (
      <PersonnelMarker
        key={`${entry.id}`}
        person={entry.person}
        personId={entry.id}
        displayName={entry.displayName}
        latLng={entry.latLng}
        timestampLabel={entry.timestampLabel}
        showLabel={!showClusters && visibleLabelIds.has(`${entry.id}`)}
        showHoverTooltip={showHoverTooltip}
        onSelect={setSelectedPersonnelId}
        isPanelOpen={isPanelOpen}
        onPanelOpen={PanelComponent ? setActivePanelPersonnelId : null}
        onPanelClose={() => setActivePanelPersonnelId(null)}
        panelContent={panelContent}
      />
    );
  });

  if (!entries.length) return null;

  if (showClusters) {
    return (
      <MarkerClusterGroup
        chunkedLoading
        chunkInterval={80}
        removeOutsideVisibleBounds
        spiderfyOnMaxZoom
        showCoverageOnHover={false}
        maxClusterRadius={55}
        iconCreateFunction={clusterIconCreateFunction}
      >
        {markers}
      </MarkerClusterGroup>
    );
  }

  return <>{markers}</>;
};

export default PersonellLayer;
